import React, { useState, useEffect, useContext } from 'react';
import Layout from '../components/Layout';
import { Users as UsersIcon, Plus, Trash2, ShieldCheck, Download, Eye, EyeOff, KeyRound, Package, Wallet, LayoutDashboard, Clock, Pencil, CheckCircle2 } from 'lucide-react';
import api from '../utils/api';
import { AuthContext } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const Users = () => {
    const { user } = useContext(AuthContext);
    const [usersList, setUsersList] = useState([]);
    const [newUsername, setNewUsername] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newRole, setNewRole] = useState('boss');
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Şifre Değiştirme
    const [passwordModal, setPasswordModal] = useState(null); // userId veya null
    const [currentPwd, setCurrentPwd] = useState('');
    const [newPwd, setNewPwd] = useState('');
    const [showPwd, setShowPwd] = useState(false);
    const [isPwdSubmitting, setIsPwdSubmitting] = useState(false);

    // Rol Değiştirme
    const [roleModal, setRoleModal] = useState(null); // { id, username, currentRole } veya null
    const [selectedRole, setSelectedRole] = useState('');
    const [isRoleSubmitting, setIsRoleSubmitting] = useState(false);

    const fetchUsers = async () => {
        setIsLoading(true);
        try {
            const res = await api.get('/auth/users');
            setUsersList(res.data);
        } catch (error) {
            console.error("Kullanıcılar alınamadı", error);
            toast.error('Kullanıcı listesi yüklenemedi.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (user?.role === 'admin') {
            fetchUsers();
        }
    }, [user?.role]);

    if (user?.role !== 'admin') {
        return <Navigate to="/dashboard" />;
    }

    const handleAddUser = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await api.post('/auth/register', {
                username: newUsername,
                password: newPassword,
                role: newRole
            });
            setNewUsername('');
            setNewPassword('');
            setNewRole('boss');
            fetchUsers();
            toast.success("Kullanıcı başarıyla oluşturuldu!");
        } catch (error) {
            toast.error(error.response?.data?.message || "Kullanıcı eklenemedi.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteUser = async (id, role) => {
        if (role === 'admin') {
            toast.error("Ana Yönetici Hesabını Silemezsiniz!");
            return;
        }

        if (window.confirm('Bu kullanıcı hesabını silmek istediğinize emin misiniz?')) {
            try {
                await api.delete(`/auth/users/${id}`);
                fetchUsers();
                toast.success('Kullanıcı hesabı silindi.');
            } catch (error) {
                toast.error("Silme işlemi başarısız.");
            }
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setIsPwdSubmitting(true);
        try {
            await api.put('/auth/change-password', {
                userId: passwordModal,
                currentPassword: passwordModal === user?.id ? currentPwd : undefined,
                newPassword: newPwd
            });
            toast.success('Şifre başarıyla güncellendi!');
            setPasswordModal(null);
            setCurrentPwd('');
            setNewPwd('');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Şifre güncellenemedi.');
        } finally {
            setIsPwdSubmitting(false);
        }
    };

    const handleChangeRole = async (e) => {
        e.preventDefault();
        if (!roleModal || !selectedRole) return;
        setIsRoleSubmitting(true);
        try {
            await api.put(`/auth/users/${roleModal.id}/role`, { role: selectedRole });
            toast.success(`${roleModal.username} kullanıcısının rolü güncellendi!`);
            setRoleModal(null);
            setSelectedRole('');
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Rol güncellenemedi.');
        } finally {
            setIsRoleSubmitting(false);
        }
    };

    const openRoleModal = (usr) => {
        setRoleModal({ id: usr.id, username: usr.username, currentRole: usr.role });
        setSelectedRole(usr.role);
    };

    const handleBackup = async () => {
        try {
            const response = await api.get('/backup', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Doviz_Backup_${new Date().toISOString().split('T')[0]}.zip`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            toast.success('Yedek başarıyla indirildi!');
        } catch (error) {
            toast.error('Yedek alma işlemi başarısız.');
        }
    };

    // YETKİ TABLOSU VERİSİ
    const permissionsData = [
        { feature: 'Finansal Özet (Dashboard)', icon: <LayoutDashboard size={16} />, admin: true, staff: true, shipment_staff: false, boss: true },
        { feature: 'Tahsilat Kayıtlarını Görme', icon: <Wallet size={16} />, admin: true, staff: true, shipment_staff: false, boss: true },
        { feature: 'Tahsilat Kaydı Ekleme', icon: <Plus size={16} />, admin: true, staff: false, shipment_staff: false, boss: false },
        { feature: 'Tahsilat Kaydı Düzenleme', icon: <Pencil size={16} />, admin: true, staff: false, shipment_staff: false, boss: false },
        { feature: 'Tahsilat Kaydı Silme', icon: <Trash2 size={16} />, admin: true, staff: false, shipment_staff: false, boss: false },
        { feature: 'Tahsilat Durumunu Değiştirme', icon: <CheckCircle2 size={16} />, admin: true, staff: false, shipment_staff: false, boss: false },
        { feature: 'Bekleyen Tahsilatlar Raporu', icon: <Clock size={16} />, admin: true, staff: true, shipment_staff: false, boss: true },
        { feature: 'Kargo Listesi Görme', icon: <Package size={16} />, admin: true, staff: true, shipment_staff: true, boss: true },
        { feature: 'Kargo Kaydı Ekleme', icon: <Plus size={16} />, admin: true, staff: true, shipment_staff: true, boss: false },
        { feature: 'Kargo Kaydı Düzenleme', icon: <Pencil size={16} />, admin: true, staff: false, shipment_staff: false, boss: false },
        { feature: 'Kargo Kaydı Silme', icon: <Trash2 size={16} />, admin: true, staff: false, shipment_staff: false, boss: false },
        { feature: 'İdari Takip Sayfasını Görme', icon: <CheckCircle2 size={16} />, admin: true, staff: false, shipment_staff: false, boss: true },
        { feature: 'İdari Takip Düzenleme', icon: <Pencil size={16} />, admin: true, staff: false, shipment_staff: false, boss: false },
        { feature: 'Satıcı Yönetimi', icon: <UsersIcon size={16} />, admin: true, staff: false, shipment_staff: false, boss: false },
        { feature: 'Kullanıcı Yönetimi', icon: <UsersIcon size={16} />, admin: true, staff: false, shipment_staff: false, boss: false },
        { feature: 'Şifre Değiştirme (Kendi)', icon: <KeyRound size={16} />, admin: true, staff: true, shipment_staff: true, boss: true },
        { feature: 'Şifre Değiştirme (Başkası)', icon: <KeyRound size={16} />, admin: true, staff: false, shipment_staff: false, boss: false },
        { feature: 'Veritabanı Yedekleme', icon: <Download size={16} />, admin: true, staff: false, shipment_staff: false, boss: false },
        { feature: 'Excel Dışa Aktarma', icon: <Download size={16} />, admin: true, staff: true, shipment_staff: false, boss: true },
    ];

    const roleLabels = {
        admin: { name: 'Yönetici', color: 'text-purple-400', bg: 'bg-purple-500/10' },
        staff: { name: 'Personel', color: 'text-blue-400', bg: 'bg-blue-500/10' },
        shipment_staff: { name: 'Saha Personeli', color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
        boss: { name: 'Boss', color: 'text-slate-200', bg: 'bg-slate-700' },
    };

    const renderCheck = (val) => val
        ? <span className="text-green-400 text-lg">✓</span>
        : <span className="text-red-500/50 text-lg">✗</span>;

    return (
        <Layout>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white mb-2">Sistem ve Kullanıcı Yönetimi</h1>
                    <p className="text-slate-400">Yeni kullanıcı ekleyin, yetkilendirme tablosunu inceleyin, veritabanı yedeği alın.</p>
                </div>

                <button
                    onClick={handleBackup}
                    className="w-full md:w-auto flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-5 py-3 rounded-xl border border-slate-600 font-bold tracking-wide transition-all shadow-lg active:scale-95"
                >
                    <Download size={20} className="text-primary-400" />
                    <span>TÜM VERİYİ YEDEKLE (ZIP)</span>
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                {/* YENİ KULLANICI EKLEME FORMU */}
                <div className="md:col-span-1 border border-slate-700/50 glass p-6 rounded-2xl h-fit">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-full bg-primary-500/10 flex items-center justify-center text-primary-400">
                            <Plus size={24} />
                        </div>
                        <h2 className="text-lg font-bold text-white">Yeni Kullanıcı Ekle</h2>
                    </div>

                    <form onSubmit={handleAddUser} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-1">Kullanıcı Adı</label>
                            <input
                                type="text" required value={newUsername} onChange={(e) => setNewUsername(e.target.value)}
                                className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
                                placeholder="Örn: ahmet_izleyici"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-1">Şifre Belirle</label>
                            <input
                                type="password" required minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                                className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
                                placeholder="En az 8 karakter"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-1">Yetki (Rol)</label>
                            <select
                                value={newRole} onChange={(e) => setNewRole(e.target.value)}
                                className="w-full px-4 py-2.5 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
                            >
                                <option value="boss">👑 Boss (Sadece İzleme + Rapor)</option>
                                <option value="staff">👤 Personel (Tahsilat + Kargo)</option>
                                <option value="shipment_staff">📦 Saha Personeli (Sadece Kargo)</option>
                            </select>
                        </div>
                        <button
                            type="submit" disabled={isSubmitting}
                            className="w-full bg-primary-600 hover:bg-primary-500 text-white font-bold py-3 rounded-lg mt-2 transition-all shadow-lg shadow-primary-500/20 disabled:opacity-50"
                        >
                            {isSubmitting ? 'Ekleniyor...' : 'Kullanıcıyı Oluştur'}
                        </button>
                    </form>
                </div>

                {/* KAYITLI KULLANICILAR LİSTESİ */}
                <div className="md:col-span-2 border border-slate-700/50 glass rounded-2xl overflow-hidden shadow-xl">
                    <div className="p-5 border-b border-slate-700/50 bg-slate-800/30">
                        <h3 className="text-lg font-bold text-white flex items-center gap-2">
                            <UsersIcon size={20} className="text-primary-400" /> Kayıtlı Kullanıcılar
                        </h3>
                    </div>

                    <div className="mobile-table-hint lg:hidden">Kullanıcı ayrıntıları ve işlemler için tabloyu yatay kaydırın →</div>
                    <div className="mobile-table-scroll overflow-x-auto">
                        <table className="mobile-complete-table w-full text-left text-sm whitespace-nowrap">
                            <thead className="bg-slate-800/80 text-slate-300 border-b border-slate-700">
                                <tr>
                                    <th className="px-6 py-4 font-semibold">Rol</th>
                                    <th className="px-6 py-4 font-semibold">Kullanıcı Adı</th>
                                    <th className="px-6 py-4 font-semibold">Kayıt Tarihi</th>
                                    <th className="px-6 py-4 font-semibold text-right">İşlem</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-800/50">
                                {isLoading ? (
                                    <tr><td colSpan="4" className="text-center py-8 text-slate-500">Yükleniyor...</td></tr>
                                ) : (
                                    usersList.map((usr) => (
                                        <tr key={usr.id} className="hover:bg-slate-800/30">
                                            <td className="px-6 py-4">
                                                {usr.role === 'admin' ? (
                                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-purple-500/10 text-purple-400 border border-purple-500/20">
                                                        <ShieldCheck size={14} /> Yönetici
                                                    </span>
                                                ) : usr.role === 'staff' ? (
                                                    <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs font-semibold">Personel</span>
                                                ) : usr.role === 'shipment_staff' ? (
                                                    <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs font-semibold">Saha Personeli</span>
                                                ) : usr.role === 'boss' ? (
                                                    <span className="px-3 py-1 bg-slate-700 text-slate-300 rounded-full text-xs font-semibold">Boss</span>
                                                ) : usr.role === 'viewer' ? (
                                                    <span className="px-3 py-1 bg-orange-500/20 text-orange-400 rounded-full text-xs font-semibold">İzleyici (Eski)</span>
                                                ) : (
                                                    <span className="px-3 py-1 bg-slate-800 text-slate-300 rounded-full text-xs font-semibold">{usr.role}</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 font-bold text-white">{usr.username}</td>
                                            <td className="px-6 py-4 text-slate-400">{new Date(usr.createdAt).toLocaleDateString('tr-TR')}</td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    {/* Rol Değiştir (Admin hariç) */}
                                                    {usr.role !== 'admin' && (
                                                        <button
                                                            onClick={() => openRoleModal(usr)}
                                                            className="p-2 text-slate-400 hover:text-yellow-400 hover:bg-yellow-500/10 rounded-lg transition-colors"
                                                            title="Rolünü Değiştir"
                                                        >
                                                            <ShieldCheck size={18} />
                                                        </button>
                                                    )}
                                                    {/* Şifre Değiştir */}
                                                    <button
                                                        onClick={() => setPasswordModal(usr.id)}
                                                        className="p-2 text-slate-400 hover:text-primary-400 hover:bg-primary-500/10 rounded-lg transition-colors"
                                                        title="Şifresini Değiştir"
                                                    >
                                                        <KeyRound size={18} />
                                                    </button>
                                                    {/* Sil (Admini silme) */}
                                                    {usr.role !== 'admin' && (
                                                        <button onClick={() => handleDeleteUser(usr.id, usr.role)} className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors" title="Hesabı Sil">
                                                            <Trash2 size={18} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* YETKİLENDİRME TABLOSU */}
            <div className="glass border border-slate-700/50 rounded-2xl overflow-hidden shadow-2xl">
                <div className="p-5 border-b border-slate-700/50 bg-slate-800/30">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <ShieldCheck size={20} className="text-primary-400" /> Yetkilendirme Matrisi
                    </h3>
                    <p className="text-xs text-slate-400 mt-1">Her rolün erişebildiği sayfalar ve işlemler</p>
                </div>
                <div className="mobile-table-hint lg:hidden">Tüm rollerin yetkilerini karşılaştırmak için tabloyu yatay kaydırın →</div>
                <div className="mobile-table-scroll overflow-x-auto">
                    <table className="mobile-complete-table w-full text-sm">
                        <thead className="bg-slate-800/80 text-slate-300 border-b border-slate-700">
                            <tr>
                                <th className="px-3 md:px-6 py-4 font-semibold text-left">Özellik / İşlem</th>
                                {Object.entries(roleLabels).map(([key, val]) => (
                                    <th key={key} className="px-2 md:px-4 py-4 text-center">
                                        <span className={`inline-block px-2 md:px-3 py-1 rounded-full text-[10px] md:text-xs font-bold ${val.bg} ${val.color}`}>
                                            {val.name}
                                        </span>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800/50">
                            {permissionsData.map((perm, i) => (
                                <tr key={i} className="hover:bg-slate-800/30 transition-colors">
                                    <td className="px-3 md:px-6 py-3.5 text-slate-200 font-medium">
                                        <div className="flex items-center gap-2.5 text-xs md:text-sm">
                                            <span className="text-slate-500 shrink-0">{perm.icon}</span>
                                            <span className="whitespace-normal">{perm.feature}</span>
                                        </div>
                                    </td>
                                    <td className="px-2 md:px-4 py-3.5 text-center">{renderCheck(perm.admin)}</td>
                                    <td className="px-2 md:px-4 py-3.5 text-center">{renderCheck(perm.staff)}</td>
                                    <td className="px-2 md:px-4 py-3.5 text-center">{renderCheck(perm.shipment_staff)}</td>
                                    <td className="px-2 md:px-4 py-3.5 text-center">{renderCheck(perm.boss)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ŞİFRE DEĞİŞTİRME MODALI */}
            {passwordModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm">
                    <div className="bg-slate-800 border border-slate-700 w-full max-w-md rounded-2xl shadow-2xl p-6">
                        <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                            <KeyRound className="text-primary-400" size={24} /> Şifre Değiştir
                        </h2>
                        <form onSubmit={handleChangePassword} className="space-y-4">
                            {/* Kendi şifresini değiştiriyorsa mevcut şifreyi sor */}
                            {passwordModal === user?.id && (
                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-1">Mevcut Şifre</label>
                                    <input
                                        type={showPwd ? 'text' : 'password'}
                                        value={currentPwd}
                                        onChange={(e) => setCurrentPwd(e.target.value)}
                                        required
                                        className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
                                    />
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-1">Yeni Şifre</label>
                                <div className="relative">
                                    <input
                                        type={showPwd ? 'text' : 'password'}
                                        value={newPwd}
                                        onChange={(e) => setNewPwd(e.target.value)}
                                        required minLength={8}
                                        className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-primary-500 pr-12"
                                        placeholder="En az 8 karakter"
                                    />
                                    <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                                        {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button type="button" onClick={() => { setPasswordModal(null); setCurrentPwd(''); setNewPwd(''); }} className="flex-1 px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors font-medium">
                                    İptal
                                </button>
                                <button type="submit" disabled={isPwdSubmitting} className="flex-1 px-4 py-2.5 bg-primary-600 hover:bg-primary-500 text-white rounded-lg font-bold transition-all disabled:opacity-50">
                                    {isPwdSubmitting ? 'Değiştiriliyor...' : 'Şifreyi Güncelle'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ROL DEĞİŞTİRME MODALI */}
            {roleModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm">
                    <div className="bg-slate-800 border border-slate-700 w-full max-w-md rounded-2xl shadow-2xl p-6">
                        <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                            <ShieldCheck className="text-yellow-400" size={24} /> Rol Değiştir
                        </h2>
                        <p className="text-slate-400 text-sm mb-6">
                            <span className="font-bold text-white">{roleModal.username}</span> kullanıcısının rolünü değiştirin.
                        </p>
                        <form onSubmit={handleChangeRole} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-1">Yeni Rol</label>
                                <select
                                    value={selectedRole}
                                    onChange={(e) => setSelectedRole(e.target.value)}
                                    className="w-full px-4 py-2.5 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
                                >
                                    <option value="boss">👑 Boss (Sadece İzleme + Rapor)</option>
                                    <option value="staff">👤 Personel (Tahsilat + Kargo)</option>
                                    <option value="shipment_staff">📦 Saha Personeli (Sadece Kargo)</option>
                                </select>
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button type="button" onClick={() => { setRoleModal(null); setSelectedRole(''); }} className="flex-1 px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors font-medium">
                                    İptal
                                </button>
                                <button type="submit" disabled={isRoleSubmitting || selectedRole === roleModal.currentRole} className="flex-1 px-4 py-2.5 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg font-bold transition-all disabled:opacity-50">
                                    {isRoleSubmitting ? 'Güncelleniyor...' : 'Rolü Güncelle'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default Users;
